import { useRef } from "react";
import { Flex, TextInput } from "@mantine/core";
import { FormRow, FormColumn } from "../common/Form/FormRow";
import Container from "../common/Container";
import PageHeader from "../common/PageHeader";
import { useForm } from "@mantine/form";
import { errorNotification, updateNotification } from "../../utils/useNotification";
import { unwrap } from "../../utils/apiResult";
import { updateEmployeeById } from "../../api/employeeApi";
import {
  combineValidators,
  validateMaxLength,
  validateMinLength,
  validateNoSpaces,
  validatePhoneNumberIsNumeric,
  validatePhoneNumberStartsWithZero,
  validateRequired,
} from "../../utils/validations";
import CustomSelect from "../common/Inputs/CustomSelect";
import CustomDatePicker from "../common/Inputs/CustomDatePicker";
import { useNavigate, useParams, useLocation } from "react-router";
import dayjs from "dayjs";
import EmployeeTagFields from "./EmployeeTagFields";
import { denormalizePhone } from "../../utils/phoneUtils";


const EmployeeUpdatePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const user = location.state?.employee;

  const form = useForm({
    initialValues: {
      name: user?.name || "",
      phone: denormalizePhone(user?.phone || ""),
      departmentIds: user?.departments?.map((d: any) => d.value) || [],
      // Tek kaynak: arka uç rol ve yeteneği `tags` içinde döndürüyor.
      tagIds: (user?.tags ?? []).map((t: any) => String(t.value)),
      contractId: user?.contract?.value || null,
      hireDate: user?.hireDate ? dayjs(user.hireDate).toDate() : null,
      isWorkingCurrently: user?.isWorkingCurrently ?? true,
      birthday: user?.birthday ? dayjs(user.birthday).toDate() : null,
    },
    validate: {
      name: validateRequired,
      phone: combineValidators(
        validateRequired,
        validatePhoneNumberStartsWithZero,
        validatePhoneNumberIsNumeric,
        validateNoSpaces,
        validateMinLength(11),
        validateMaxLength(11)
      ),
    },
  });

  const handleSubmit = async (values: object) => {
    try {
      const result = unwrap(
        await updateEmployeeById({ id: id || user?._id, updates: values })
      );
      if (result.ok) {
        updateNotification(true);
        navigate(-1);
        return;
      }
      // Eskiden updateNotification(undefined) çağrılıyordu; sunucu 403/500
      // dönerse kullanıcı hiçbir şey görmüyordu.
      errorNotification({ message: result.message ?? undefined });
    } catch (error) {
      console.error("Error:", error);
      errorNotification();
    }
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Çalışan Bilgisi Düzenleme"
        isButtoned
        buttonText="Güncelle"
        buttonOnClick={() => formRef.current?.requestSubmit()}
        buttonDisabled={!form.isDirty()}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
        <form
          ref={formRef}
          style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <FormRow $gap={50}>
            <FormColumn>
              <TextInput
                label="Ad Soyad"
                placeholder="Ad Soyad Giriniz."
                radius={10}
                maxLength={40}
                w={"100%"}
                {...form.getInputProps("name")}
                withAsterisk
              />
              <TextInput
                label="Telefon"
                placeholder="05*********"
                radius={10}
                type="tel"
                w={"100%"}
                maxLength={11}
                {...form.getInputProps("phone")}
                withAsterisk
              />
              <CustomDatePicker
                label="İşe Giriş Tarihi"
                placeholder="Tarih seçiniz."
                w="100%"
                {...form.getInputProps("hireDate")}
              />
              <CustomDatePicker
                label="Doğum Tarihi"
                placeholder="Tarih seçiniz."
                w="100%"
                {...form.getInputProps("birthday")}
              />
            </FormColumn>

            <FormColumn>
              <EmployeeTagFields form={form} />
              <CustomSelect
                label="Çalışma Durumu"
                placeholder="Durum seçiniz."
                w="100%"
                value={form.values.isWorkingCurrently?.toString()}
                onChange={(val: any) =>
                  form.setFieldValue("isWorkingCurrently", val === "true")
                }
                data={[
                  { value: "true", label: "Çalışıyor" },
                  { value: "false", label: "Ayrıldı" },
                ]}
              />
            </FormColumn>
          </FormRow>
        </form>
      </div>
    </Container>
  );
};

export default EmployeeUpdatePage;
